// AquaFlow training / blind test: hide a leak, create "measured" values from it, let the search run without it, then compare
import { uid, pipeLatLngs } from './model';
import { simulate } from './aquaNet';
import { srcFits } from './aquaLeakFind';
import { isHouse } from './netCheck';

const M = 111320;
export const distM = (a, b) => { const k = Math.cos((a[0] * Math.PI) / 180); return Math.hypot((a[0] - b[0]) * M, (a[1] - b[1]) * M * k); };
const gauss = () => { let u = 0, v = 0; while (!u) u = Math.random(); while (!v) v = Math.random(); return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v); };
const water = (p) => (p.type === 'water' || !p.type) && p.plan !== 'removed' && p.status !== 'abandoned';

// leak object on a pipe; height from the nearest object on that pipe (no terrain model mixed in)
export function mkLeak(project, pt, pipe, name, hyd = { leakOn: true, lmode: 'fixed', lq: 0.5, ld: 5 }) {
  const k = Math.cos((pt[0] * Math.PI) / 180), F = project.fittings || [];
  const ends = pipe.points.map((x) => x?.fid && F.find((o) => o.id === x.fid)).filter((o) => o && o.elev?.v != null);
  const near = ends.sort((a, b) => Math.hypot(a.lat - pt[0], (a.lon - pt[1]) * k) - Math.hypot(b.lat - pt[0], (b.lon - pt[1]) * k))[0];
  return { id: uid('fit'), type: 'leak', name, lat: pt[0], lon: pt[1], state: 'defect', severity: 'medium', hostPipe: pipe.id, hostDn: pipe.dn || '', depth: pipe.depth ?? 1.5, ...(near ? { elev: { v: near.elev.v, src: near.elev.src || 'net' } } : {}), media: [], memos: [], hyd };
}

// random position on a main / supply pipe (length-weighted, not on house connections), 0.3–1.5 l/s
export function randomLeak(project, name) {
  const F = project.fittings || [], byId = Object.fromEntries(F.map((f) => [f.id, f]));
  const segs = [];
  (project.pipes || []).filter((p) => water(p) && p.type !== 'house' && !p.points.some((x) => isHouse(byId[x?.fid]))).forEach((p) => {
    const ll = pipeLatLngs(p, F);
    for (let i = 1; i < ll.length; i++) { const len = distM(ll[i - 1], ll[i]); if (len > 2) segs.push({ p, a: ll[i - 1], b: ll[i], len }); }
  });
  if (!segs.length) return null;
  let r = Math.random() * segs.reduce((s, x) => s + x.len, 0), sg = segs[0];
  for (const x of segs) { r -= x.len; if (r <= 0) { sg = x; break; } }
  const u = 0.15 + Math.random() * 0.7, pt = [sg.a[0] + (sg.b[0] - sg.a[0]) * u, sg.a[1] + (sg.b[1] - sg.a[1]) * u];
  return mkLeak(project, pt, sg.p, name, { leakOn: true, lmode: 'fixed', lq: +(0.3 + Math.random() * 1.2).toFixed(2), ld: 5 });
}

// hydrants to "measure": the ones already chosen in the leak search, else up to 8 spread over the list
export function measIds(project, ls) {
  const ids = Object.keys(ls.hyd || {});
  if (ids.length) return ids;
  const H = (project.fittings || []).filter((f) => f.type === 'hydrant');
  return H.filter((_, i) => i % Math.max(1, Math.round(H.length / 8)) === 0).slice(0, 8).map((f) => f.id);
}

// simulate with the hidden leaks -> synthetic logger values (optional noise like real loggers: σ 0.01 bar, σ 1 % inflow)
export async function hiddenMeas(project, cfg, leaks, ids, noise) {
  const p = { ...project, fittings: [...(project.fittings || []).filter((f) => f.type !== 'leak'), ...leaks] };
  const r = await simulate(p, cfg, { noHydRes: true });
  if (r.error) return { error: r.error };
  const src = {}, hyd = {};
  srcFits(p).forEach((f) => { const n = r.nodes[f.id]; if (n) src[f.id] = +((-n.demand) * (noise ? 1 + 0.01 * gauss() : 1)).toFixed(2); });
  ids.forEach((id) => { const b = r.nodes[id]?.bar; if (Number.isFinite(b)) hyd[id] = +(b + (noise ? 0.01 * gauss() : 0)).toFixed(2); });
  const q = Object.fromEntries((r.leaks || []).map((x) => [x.fid, x.q]));
  return { src, hyd, q };
}

// shortest distance from a point to a polyline (metres, local plane)
const toLine = (p, cs) => {
  const k = Math.cos((p[0] * Math.PI) / 180), P = (c) => [(c[0] - p[0]) * M, (c[1] - p[1]) * M * k];
  let best = Infinity;
  for (let i = 1; i < cs.length; i++) {
    const a = P(cs[i - 1]), b = P(cs[i]), dx = b[0] - a[0], dy = b[1] - a[1], L = dx * dx + dy * dy;
    const u = L ? Math.max(0, Math.min(1, -(a[0] * dx + a[1] * dy) / L)) : 0;
    best = Math.min(best, Math.hypot(a[0] + u * dx, a[1] + u * dy));
  }
  return cs.length === 1 ? distM(p, cs[0]) : best;
};

// compare the search result with the hidden truth
export function grade(res, secret) {
  if (!res || res.error) return null;
  const top = res.top || [];
  const per = secret.leaks.map((l) => {
    const i = top.findIndex((x) => x.pipeId === l.hostPipe);
    const near = top.length ? Math.min(...top.slice(0, 3).map((x) => toLine([l.lat, l.lon], x.coords?.length ? x.coords : [x.mid]))) : null;
    return { id: l.id, name: l.name, rank: i < 0 ? null : i + 1, prob: i < 0 ? 0 : top[i].prob, near, q: secret.q?.[l.id] ?? l.hyd?.lq };
  });
  const qTrue = per.reduce((s, x) => s + (+x.q || 0), 0), best = Math.min(...per.map((x) => x.rank ?? 99));
  const level = res.none ? 'miss' : best === 1 ? 'hit' : best <= 3 ? 'near' : 'miss';
  return { per, qTrue, qEst: res.qLeak, level, discr: res.discr, flat: !!res.flat, nH: res.nH };
}
